import React, { useMemo } from "react";
import {
  Autocomplete,
  Checkbox,
  TextField,
} from "@mui/material";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

const getId = (option) =>
  option?.id ?? option?.Id ?? option?.value ?? option ?? null;

const MultiSelectField = ({
  label,
  placeholder,
  value,
  options,
  onChange,
  noOptionsText,
  disabled = false
}) => {
  const optionMap = useMemo(() => {
    const map = new Map();
    (options || []).forEach((option) => {
      const id = getId(option);
      if (id !== null && id !== undefined) {
        map.set(id, option);
      }
    });
    return map;
  }, [options]);

  const resolvedValue = useMemo(() => {
    return (value || []).map((selected) => {
      const id = getId(selected);
      return optionMap.get(id) || selected;
    });
  }, [value, optionMap]);

  const resolveLabel = (option) => {
    if (!option) {
      return "";
    }

    const baseLabel =
      option.text ?? option.name ?? option.label ?? "";

    if (baseLabel && !/^ID\s+/i.test(baseLabel)) {
      return baseLabel;
    }

    const match = optionMap.get(getId(option));
    return (
      match?.text ??
      match?.name ??
      match?.label ??
      baseLabel ??
      (getId(option) !== null && getId(option) !== undefined
        ? `ID ${getId(option)}`
        : "")
    );
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={options || []}
      disabled={disabled}
      value={resolvedValue}
      onChange={(event, newValue) => onChange(newValue || [])}
      getOptionLabel={resolveLabel}
      isOptionEqualToValue={(option, selected) =>
        getId(option) === getId(selected)
      }
      noOptionsText={noOptionsText}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            icon={checkboxIcon}
            checkedIcon={checkboxCheckedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          {resolveLabel(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          margin="normal"
          disabled={disabled}
        />
      )}
    />
  );
};

export default MultiSelectField;